import React, { useEffect, useState } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Layout from "./Pages/Layout";
import Calendar from "./Pages/Calendar";
import LogIn from "./Pages/LogIn";
import Agenda from "./Pages/Agenda";
import Todo from "./Pages/Todo";
import Settings from "./Pages/Settings";
import Resources from "./Pages/Resources";
import PlanMission from "./Components/Pilot/PlanMission";
import OnLocation from "./Components/Pilot/OnLocation";
import Operations from "./Components/Pilot/Operations";
import PostFlight from "./Components/Pilot/PostFlight";
import Update from "./Pages/Update";
import DataAnalysis from './Pages/DataAnalysis';
import ProjectEquipment from "./Components/Brent/ProjectEquipment";
import CustomerLogin from "./Pages/CustomerLogin";
import "./index.css";
import "./Pages/Layout.css";
import "./styles/electron.css";
import { ProtectedRoute } from "./Components/ProtectedRoutes";
import { CustomerProtectedRoute } from "./Components/CustomerProtectedRoute";
import { AuthProvider, useAuth } from "./Hooks/AuthProvider.jsx";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { environment } from "./config/environment";
import { LiveblocksProvider } from "@liveblocks/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import NotificationsProvider from "./Components/NotificationsProvider.jsx";
import { useNotifications } from "./Components/NotificationsProvider.jsx";
import { getQueue, setQueue, removeItem } from "./Utils/offlineQueue";

const CUSTOMER_HOSTS = ["kunde.brent.as", "customer.brent.as"];

// Swap favicon based on host (Motion Air vs Brent)
const setFavicon = (href) => {
  if (typeof document === "undefined") return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
};

// Redirect customer host traffic to the customer portal routes while keeping the host URL
const CustomerHostRouterGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCustomerHost =
    typeof window !== "undefined" && CUSTOMER_HOSTS.includes(window.location.hostname);

  useEffect(() => {
    console.log("[CustomerHostRouterGuard] host:", window.location.hostname, "path:", location.pathname, "isCustomerHost:", isCustomerHost);
    if (!isCustomerHost) return;
    if (location.pathname.startsWith("/customer")) return;
    console.log("[CustomerHostRouterGuard] redirecting to /customer-login");
    navigate("/customer-login", { replace: true });
  }, [isCustomerHost, location.pathname, navigate]);

  return null;
};


/**
 * This new component waits for a user to be authenticated before it
 * creates the Liveblocks client. This solves the timing issue.
 */
// Component to handle Electron app ready signal when auth is complete
const ElectronReadyHandler = ({ children }) => {
  const { loading, user, handleLogout } = useAuth();

  // Add Electron-specific class to body
  useEffect(() => {
    if (environment.platform === 'electron') {
      // Add electron-app class to body for CSS targeting
      document.body.classList.add('electron-app');
      console.log('[Electron CSS] Added electron-app class to body');
      
      // Cleanup on unmount
      return () => {
        document.body.classList.remove('electron-app');
      };
    }
  }, []);

  useEffect(() => {
    console.log('[App] Auth state changed - loading:', loading, 'user:', user ? 'present' : 'none');
    console.log('[App] Environment platform:', environment.platform);
    console.log('[App] electronAPI available:', !!window.electronAPI);
    console.log('[App] appReady available:', !!window.electronAPI?.appReady);
    
    if (environment.platform === 'electron' && window.electronAPI?.appReady && !loading) {
      console.log('[App] Auth loading complete, signaling app ready to Electron');
      window.electronAPI.appReady()
        .then(() => console.log('[App] App ready signal sent successfully'))
        .catch(error => console.error('[App] Failed to send app ready signal:', error));
    }
  }, [loading, user]);

  // Listen for sign out requests from settings window
  useEffect(() => {
    if (environment.platform === 'electron' && window.electronAPI?.onSignOutRequest) {
      const handleSignOutRequest = () => {
        console.log('[App] Sign out requested from settings window');
        // Use the proper logout function from auth context
        handleLogout();
      };

      window.electronAPI.onSignOutRequest(handleSignOutRequest);

      // Cleanup listener on unmount
      return () => {
        if (window.electronAPI?.removeListener) {
          window.electronAPI.removeListener('sign-out-request', handleSignOutRequest);
        }
      };
    }
  }, [handleLogout]);

  // Clear any active verifier listeners on logout/auth change by unmounting/reinitializing children tree
  return <>{children}</>;
};

const LiveblocksClientProvider = ({ children }) => {
  const { user } = useAuth();
  const [, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!user) {
    return <>{children}</>;
  }

  // Keep LiveblocksProvider mounted even when offline to avoid context errors in RoomProvider

  const functions = getFunctions(undefined, 'us-central1');
  const functionName = 'liveblocksAuth';
  const authFunction = httpsCallable(functions, functionName);

  const authEndpoint = async (room) => {
    try {
      const { data } = await authFunction({ room });
      return data;
    } catch (error) {
      console.error("🔥 Liveblocks authentication failed:", error);
      throw error;
    }
  };

  return <LiveblocksProvider authEndpoint={authEndpoint}>{children}</LiveblocksProvider>;
};

const AppRoutes = () => (
    <>
      <CustomerHostRouterGuard />
      <Routes>
        <Route path="/" element={<Layout hideHeaderPaths={["/"]} />}>
          <Route index element={<LogIn />} />
          <Route path="calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
          <Route path="agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
          <Route path="todo" element={<ProtectedRoute><Todo /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
          <Route path="pilot" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
          <Route path="pilot/plan" element={<ProtectedRoute><PlanMission /></ProtectedRoute>} />
          <Route path="pilot/location" element={<ProtectedRoute><OnLocation /></ProtectedRoute>} />
          <Route path="pilot/operations" element={<ProtectedRoute><Operations /></ProtectedRoute>} />
          <Route path="pilot/post" element={<ProtectedRoute><PostFlight /></ProtectedRoute>} />
          {/* Project Equipment: employees use ProtectedRoute, customers use CustomerProtectedRoute */}
          <Route path="brent/project-equipment" element={<ProtectedRoute><ProjectEquipment /></ProtectedRoute>} />
          <Route path="data-analysis" element={<ProtectedRoute><DataAnalysis /></ProtectedRoute>} />
        </Route>
        {/* Customer routes - separate from main app */}
        <Route path="/customer-login" element={<CustomerLogin />} />
        <Route path="/customer/project-equipment" element={<CustomerProtectedRoute><ProjectEquipment /></CustomerProtectedRoute>} />
        <Route path="/update" element={<Update />} />
      </Routes>
    </>
);

const App = () => {
  const theme = createTheme({
    palette: { primary: { main: "#3498B7" }, secondary: { main: "#2E2E2E" } },
    typography: { fontFamily: [ "Montserrat", "sans-serif", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "Roboto", '"Helvetica Neue"', "Arial", "sans-serif", '"Apple Color Emoji"', '"Segoe UI Emoji"', '"Segoe UI Symbol"', ].join(","), },
    breakpoints: { values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920, }, },
  });

  // Hard host guard to force customer hosts to customer login before router mounts
  useEffect(() => {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const isCustomerHost = CUSTOMER_HOSTS.includes(host);
    if (isCustomerHost && typeof window !== "undefined" && !window.location.pathname.startsWith("/customer")) {
      console.log("[App host guard] hard redirect to /customer-login");
      window.location.replace("/customer-login");
    }
  }, []);

  // Host-based favicon swap (Motion Air default, Brent for customer hosts) and tab title
  useEffect(() => {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const isCustomerHost = CUSTOMER_HOSTS.includes(host);
    // Use dedicated Brent favicon on customer hosts, Motion Air default otherwise
    const icon = isCustomerHost ? "/images/icons/brent-favicon-48x48.png" : "/favicon.ico";
    setFavicon(icon);
    console.log("[Favicon] host:", host, "icon:", icon);

    // Update tab title per host
    const title = isCustomerHost ? "Brent Customer Portal" : "Motion Air Calendar";
    if (typeof document !== "undefined") {
      document.title = title;
      const body = document.body;
      if (body) {
        body.classList.toggle("theme-brent", isCustomerHost);
        body.classList.toggle("theme-motion", !isCustomerHost);
      }
    }
    console.log("[Title] host:", host, "title:", title);
  }, []);

  const RouterComponent = environment.platform === 'electron' ? HashRouter : BrowserRouter;

  return (
    <RouterComponent>
      <AuthProvider>
        <ElectronReadyHandler>
          <NotificationsProvider>
            <LiveblocksClientProvider>
              <ThemeProvider theme={theme}>
                <OfflineQueueBridge />
                <QueueSyncVerifier />
                {/* Keep legacy offline queue badge for now (only offline) */}
                <AppRoutes />
              </ThemeProvider>
            </LiveblocksClientProvider>
          </NotificationsProvider>
        </ElectronReadyHandler>
      </AuthProvider>
    </RouterComponent>
  );
};

export default App;

// Small badge to indicate offline status
const _OfflineBadge = () => { // eslint-disable-line no-unused-vars
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div style={{
      position: 'fixed',
      right: 12,
      bottom: 12,
      background: 'rgba(200, 30, 30, 0.95)',
      color: '#fff',
      padding: '6px 10px',
      borderRadius: 8,
      fontSize: 12,
      zIndex: 9999,
      boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
    }}>
      Offline
    </div>
  );
};

// Headless bridge: turn offline queue items into notifications for the bell
/**
 * Bridges the offline queue (localStorage) into ephemeral notifications.
 * De-dupes by queue key and persists a seen set so reloads don’t duplicate.
 */
const OfflineQueueBridge = () => {
  const { add } = useNotifications() || {};
  const [tick, setTick] = useState(0);
  const lastCountRef = React.useRef(0);
  const seenKeysRef = React.useRef(new Set());
  const SEEN_KEYS_STORAGE = 'offlineQueueSeenKeys';

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1500);
    return () => clearInterval(id);
  }, []);

  // Bootstrap seen keys on mount so we don't re-notify pre-existing items after reload
  useEffect(() => {
    try {
      const persisted = JSON.parse(localStorage.getItem(SEEN_KEYS_STORAGE) || '[]');
      if (Array.isArray(persisted)) {
        persisted.forEach((k) => seenKeysRef.current.add(k));
      }
      // Also treat currently queued items as already seen at startup
      const q = getQueue();
      if (Array.isArray(q)) {
        for (const it of q) {
          seenKeysRef.current.add(it.key);
        }
      }
      localStorage.setItem(SEEN_KEYS_STORAGE, JSON.stringify(Array.from(seenKeysRef.current)));
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!add) return;
    const q = getQueue();
    if (q && q.length) {
      for (const it of q) {
        if (seenKeysRef.current.has(it.key)) continue;
        seenKeysRef.current.add(it.key);
        try {
          localStorage.setItem(SEEN_KEYS_STORAGE, JSON.stringify(Array.from(seenKeysRef.current)));
        } catch (_) {}
        add({
          id: `queue-${it.key}`,
          title: `${it.type} ${it.entity || 'item'}`,
          message: it.title || it.id,
          queueKey: it.key,
          queueItem: it,
        });
      }
    }
    lastCountRef.current = q ? q.length : 0;
  }, [tick, add]);

  // Polling verifier disabled to avoid races; rely on QueueSyncVerifier only.

  return null;
};

// Verified sync using short-lived snapshot listeners (least spammy, server-ack driven)
/**
 * Verified sync using short-lived snapshot listeners.
 * Subscribes per queued item and marks as "synced" only after server ack.
 */
const QueueSyncVerifier = () => {
  const { update } = useNotifications() || {};
  const listenersRef = React.useRef(new Map()); // key -> unsubscribe
  const scanRef = React.useRef(false);

  const startListener = React.useCallback(async (item) => {
    try {
      const { collection, query, where, onSnapshot } = await import('firebase/firestore');
      const { database } = await import('./Utils/firebaseConfig');
      const q = query(collection(database, item.collection), where('id', '==', item.id));
      const unsub = onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
        try {
          const fromServer = snap.metadata && snap.metadata.fromCache === false;
          if (!fromServer) return; // wait for server confirmation
          let isSynced = false;
          if (item.type === 'delete') {
            isSynced = snap.empty;
          } else {
            // create/update: any doc present from server counts as synced
            isSynced = !snap.empty && snap.docs[0] && snap.docs[0].metadata && snap.docs[0].metadata.hasPendingWrites === false;
          }
          if (isSynced) {
            const notifId = `queue-${item.key}`;
            update?.(notifId, { queueItem: { ...item, status: 'synced' } });

            // If this item is a delete and it's now confirmed by server,
            // remove any earlier queued ops for the same entity/id
            if (item.type === 'delete' && item.entity && item.id) {
              try {
                const all = getQueue();
                const toDrop = all.filter((qit) => qit.key !== item.key && qit.entity === item.entity && qit.id === item.id);
                if (toDrop.length) {
                  const pruned = all.filter((qit) => !(qit.entity === item.entity && qit.id === item.id && qit.key !== item.key));
                  setQueue(pruned);
                  // Mark older ops as resolved (synced) in notifications instead of removing them
                  toDrop.forEach((d) => {
                    const nId = `queue-${d.key}`;
                    update?.(nId, { queueItem: { ...d, status: 'synced', supersededBy: item.key } });
                    const u2 = listenersRef.current.get(d.key);
                    if (u2) { try { u2(); } catch (_) {} }
                    listenersRef.current.delete(d.key);
                  });
                }
              } catch (_) {}
            }

            removeItem(item.key);
            const u = listenersRef.current.get(item.key);
            if (u) { try { u(); } catch (_) {} }
            listenersRef.current.delete(item.key);
          }
        } catch (_) {}
      }, (err) => {
        // On error, drop listener; verifier may restart later
        const u = listenersRef.current.get(item.key);
        if (u) { try { u(); } catch (_) {} }
        listenersRef.current.delete(item.key);
      });
      listenersRef.current.set(item.key, unsub);
    } catch (_) {}
  }, [update]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    let mounted = true;
    const scan = async () => {
      if (!mounted || scanRef.current) return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      scanRef.current = true;
      try {
        const q = getQueue();
        if (q && q.length) {
          for (const it of q) {
            if (!listenersRef.current.has(it.key)) {
              await startListener(it);
            }
          }
        }
      } finally {
        scanRef.current = false;
      }
    };

    const id = setInterval(scan, 3000);
    // initial
    void scan();
    const listeners = listenersRef.current;
    return () => { mounted = false; clearInterval(id); listeners.forEach((u) => { try { u(); } catch (_) {} }); listeners.clear(); };
  }, [startListener]);

  return null;
};